import React, { useState } from "react";
import Header from "../components/Header";
import { distributorService } from "../services/apiService";
import Toast from "../components/Toast";

const DistributorBillCreate = () => {
  const [distributorMobileNo, setDistributorMobileNo] = useState("");
  const [distributor, setDistributor] = useState(null);
  const [distributorLoading, setDistributorLoading] = useState(false);
  
  const [billForm, setBillForm] = useState({
    billNo: "",
    billAmount: "",
    paidAmount: "",
    restAmount: "",
    paymentType: "",
    transactionId: "",
    billDate: new Date().toISOString().split('T')[0]
  });
  
  const [mobileError, setMobileError] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [toast, setToast] = useState(null);

  const showMessage = (type, text, ms = 5000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), ms);
  };

  const handleDistributorSearch = async () => {
    setMobileError("");
    if (!distributorMobileNo.trim()) return;

    setDistributorLoading(true);
    try {
      const response = await distributorService.getByMobile(distributorMobileNo.trim());
      if (response) {
        setDistributor(response);
      } else {
        setDistributor(null);
        setMobileError("Distributor not found");
      }
    } catch (err) {
      setDistributor(null);
      setMobileError("Distributor not found");
    } finally {
      setDistributorLoading(false);
    }
  };

  const handleBillChange = (e) => {
    const { name, value } = e.target;
    setBillForm(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate rest amount
      if (name === "billAmount" || name === "paidAmount") {
        const bill = parseFloat(name === "billAmount" ? value : updated.billAmount) || 0;
        const paid = parseFloat(name === "paidAmount" ? value : updated.paidAmount) || 0;
        updated.restAmount = (bill - paid).toFixed(2);
      }
      
      return updated;
    });
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!distributorMobileNo.trim()) {
      setMobileError("Mobile no is required");
      newErrors.mobileNo = true;
    } else if (!distributor) {
      setMobileError("Distributor not found");
      newErrors.mobileNo = true;
    }

    if (!billForm.billNo.trim()) newErrors.billNo = "Bill number is required";
    if (!billForm.billAmount || parseFloat(billForm.billAmount) <= 0) newErrors.billAmount = "Bill amount is required";
    if (billForm.paidAmount === "" || parseFloat(billForm.paidAmount) < 0) newErrors.paidAmount = "Paid amount is required";
    if (!billForm.paymentType) newErrors.paymentType = "Payment type is required";
    if (!billForm.billDate) newErrors.billDate = "Bill date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      await distributorService.createBill({
        distributorId: distributor.distributorId,
        billNumber: billForm.billNo,
        billAmount: parseFloat(billForm.billAmount),
        billDate: new Date(billForm.billDate).toISOString(),
        paymentStatus: billForm.paymentType,
        transactionId: billForm.transactionId.trim() || null,
        paidAmount: parseFloat(billForm.paidAmount) || 0,
      });
      setToast({ identifier: `Bill No: ${billForm.billNo}`, message: "Distributor bill created successfully!" });
      
      // Reset form
      setBillForm({
        billNo: "",
        billAmount: "",
        paidAmount: "",
        restAmount: "",
        paymentType: "",
        transactionId: "",
        billDate: new Date().toISOString().split('T')[0]
      });
      setDistributor(null);
      setDistributorMobileNo("");
    } catch (err) {
      setToast({ type: "error", identifier: `Bill No: ${billForm.billNo}`, message: err.response?.data?.message || "Failed to create bill" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBillForm({
      billNo: "",
      billAmount: "",
      paidAmount: "",
      restAmount: "",
      paymentType: "",
      transactionId: "",
      billDate: new Date().toISOString().split('T')[0]
    });
    setDistributor(null);
    setDistributorMobileNo("");
    setMobileError("");
    setErrors({});
    setMessage({ type: "", text: "" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-6">
      <Header showSearch={false} showCart={false} />

      {toast && <Toast type={toast.type} identifier={toast.identifier} message={toast.message} onClose={() => setToast(null)} />}

      {/* Bill Create Section */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-6">
        <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <div className="flex items-center gap-3">
            <i className="fas fa-file-invoice text-indigo-600" />
            <h2 className="font-bold text-blue-800">Create Distributor Bill</h2>
          </div>
          <div className="flex items-center gap-2">
            {mobileError && <p className="text-red-500 text-xs whitespace-nowrap">{mobileError}</p>}
            <input type="tel" value={distributorMobileNo}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setDistributorMobileNo(value);
                if (mobileError) setMobileError("");
                if (errors.mobileNo) setErrors(prev => ({ ...prev, mobileNo: undefined }));
              }}
              onBlur={handleDistributorSearch}
              placeholder="Distributor Mobile No"
              maxLength="10"
              className={`w-40 px-3 py-1.5 border rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white ${
                mobileError ? "border-red-400 bg-red-50" : "border-blue-300"
              }`} />
            {distributorLoading && <i className="fas fa-spinner fa-spin text-blue-400 text-xs" />}
          </div>
        </div>
        <div className="p-4">
          <form onSubmit={handleSubmit}>
            {/* Distributor id stored silently in state */}

            {/* Bill Fields - 3 per row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bill Number <span className="text-red-500">*</span></label>
                <input type="text" name="billNo" value={billForm.billNo} onChange={handleBillChange}
                  placeholder="Enter bill number"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.billNo ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.billNo && <p className="text-red-500 text-xs mt-1">{errors.billNo}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bill Date <span className="text-red-500">*</span></label>
                <input type="date" name="billDate" value={billForm.billDate} onChange={handleBillChange}
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.billDate ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.billDate && <p className="text-red-500 text-xs mt-1">{errors.billDate}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Type <span className="text-red-500">*</span></label>
                <select name="paymentType" value={billForm.paymentType} onChange={handleBillChange}
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.paymentType ? "border-red-400 bg-red-50" : "border-blue-300"}`}>
                  <option value="">Select payment type</option>
                  <option value="CASH">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
                {errors.paymentType && <p className="text-red-500 text-xs mt-1">{errors.paymentType}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bill Amount <span className="text-red-500">*</span></label>
                <input type="number" name="billAmount" value={billForm.billAmount} onChange={handleBillChange}
                  step="0.01" min="0" placeholder="0.00"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.billAmount ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.billAmount && <p className="text-red-500 text-xs mt-1">{errors.billAmount}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Paid Amount <span className="text-red-500">*</span></label>
                <input type="number" name="paidAmount" value={billForm.paidAmount} onChange={handleBillChange}
                  step="0.01" min="0" placeholder="0.00"
                  className={`w-52 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${
                    errors.paidAmount ? "border-red-400 bg-red-50" : "border-blue-300"}`} />
                {errors.paidAmount && <p className="text-red-500 text-xs mt-1">{errors.paidAmount}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rest Amount</label>
                <input type="text" value={billForm.restAmount} readOnly placeholder="0.00"
                  className="w-52 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Transaction ID</label>
                <input type="text" name="transactionId" value={billForm.transactionId} onChange={handleBillChange}
                  placeholder="Enter transaction ID"
                  className="w-52 px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={handleReset}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
                <i className="fas fa-redo" /> Reset
              </button>
              <button type="submit" disabled={loading}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                  loading ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
                {loading ? <><i className="fas fa-spinner fa-spin" />Saving...</> : <><i className="fas fa-save" />Save</>}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default DistributorBillCreate;
